/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ContactsHubSpot.tsx - INTEGRADO CON GEOMARKETING
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 1.3.0
 * @description Gestión de contactos estilo HubSpot profesional con GeoMap
 * 
 * NUEVO EN v1.3.0:
 * ✅ ContactsGeoBuilder integrado en modo Visual
 * ✅ Botón "GeoMap" funcional
 * ✅ Vista geográfica de contactos
 * 
 * FORMAS DE AGREGAR CONTACTOS:
 * 1. ✅ CSV Import (UploadCSVModal) - Upload masivo
 * 2. ✅ Constructor Manual (AddContactManualModal) - Individual
 * 3. ✅ Modo IA (AddContactsAIModal) - Texto natural con NLP
 * 4. ✅ GeoMap (ContactsGeoBuilder) - Vista geográfica interactiva
 * 
 * @date 2024-11-13
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Modal, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDatabase,
  faFileUpload,
  faMagic,
  faRobot,
  faSearch,
  faPlus,
  faDownload,
  faFilter,
  faChevronLeft,
  faChevronRight,
  faSort,
  faSortUp,
  faSortDown,
  faCheck,
  faTimesCircle,
  faCheckCircle,
  faExclamationTriangle,
  faTimes,
  faTrash,
  faTags,
  faUsers,
  faEdit,
  faMapMarkedAlt,
  faLock
} from '@fortawesome/free-solid-svg-icons';

// Hooks y utilidades
import { useContactsDatabase } from '../../../hooks/useContactsDatabase';
import type { ContactDB } from '../../../data/mockContactsDatabase';

// Estilos
import styles from '../../../types/ContactsHubSpot.module.css';

// Componentes
import UploadCSVModal from './UploadCSVModal';
import ExportDropdown from './ExportDropdown';
import AddContactManualModal from './AddContactManualModal';
import AddContactsAIModal from './AddContactsAIModal';
import ContactsGeoBuilder from './ContactsGeoBuilder'; // ← NUEVO

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface ContactsHubSpotProps {
  language?: 'es' | 'en';
  onContactsSelected?: (contacts: ContactDB[]) => void;
  onContactsReady?: (contacts: any[]) => void;
}

type ViewMode = 'database' | 'import' | 'visual' | 'ai';
type SortField = 'name' | 'phone' | 'email' | 'status' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface FilterState {
  search: string;
  status: string;
  group: string;
  tag: string;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES PARA AUDIENCIAS Y CANVAS
// ═══════════════════════════════════════════════════════════════════════════

interface SavedAudience {
  id: string;
  name: string;
  description?: string;
  filters: CanvasFilter[];
  contactCount: number;
  createdAt: string;
  color: string;
}

interface CanvasFilter {
  type: 'group' | 'tag' | 'status' | 'company' | 'age' | 'location' | 'lastContact';
  value: any;
  label: string;
}

interface CanvasElement {
  id: string;
  type: 'favorites' | 'all-contacts' | 'group' | 'audience' | 'filter';
  label: string;
  icon: string;
  position: { x: number; y: number };
  color: string;
  data?: any; // Config específica del elemento
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const ContactsHubSpot: React.FC<ContactsHubSpotProps> = ({
  language = 'es',
  onContactsSelected,
  onContactsReady
}) => {

  // ───────────────────────────────────────────────────────────────────────
  // HOOKS
  // ───────────────────────────────────────────────────────────────────────

  const {
    contacts,
    groups,
    tags,
    stats,
    isLoading,
    selectedContacts,
    toggleContactSelection,
    selectAll,
    clearSelection,
    getSelectedContactsData,
    checkDuplicatePhone,
    addContact,
    updateContactLocation // ← NUEVO: Para actualizar ubicación desde mapa
  } = useContactsDatabase();
  
  // ───────────────────────────────────────────────────────────────────────
  // CONTACTOS DERIVADOS
  // ───────────────────────────────────────────────────────────────────────

  // Mock Matilde para testing de geo
  const mockMatilde: ContactDB = {
    id: 'mock-matilde-temp',
    name: 'Matilde Villarroel',
    phone: '+56981925986',
    tags: ['vip'],
    groups: [],
    status: 'active',
    source: 'manual',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    location: {
      lat: -33.6986657,
      lng: -71.0854323,
      source: 'manual'
    }
  };

  const favoritesContacts = useMemo(() => {
    const vips = contacts.filter(c => c.tags.includes('vip') || c.tags.includes('favorite'));
    // Agregar mockMatilde si no está ya en contacts
    if (!contacts.find(c => c.id === mockMatilde.id)) {
      return [mockMatilde, ...vips];
    }
    return vips;
  }, [contacts]);

  const contactsForGeo = useMemo(() => {
    if (!contacts.find(c => c.id === mockMatilde.id)) {
      return [mockMatilde, ...contacts];
    }
    return contacts;
  }, [contacts]);

  // ───────────────────────────────────────────────────────────────────────
  // ESTADOS
  // ───────────────────────────────────────────────────────────────────────

  // UI
  const [viewMode, setViewMode] = useState<ViewMode>('database');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showGeoMap, setShowGeoMap] = useState(false); // ← NUEVO: Control del mapa

  // Filtros y búsqueda
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: 'all',
    group: 'all',
    tag: 'all'
  });

  // Tabla
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Modales
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddManualModal, setShowAddManualModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [geoBuilderContacts, setGeoBuilderContacts] = useState<ContactDB[]>([]);

  // ───────────────────────────────────────────────────────────────────────
  // ESTADOS PARA CONSTRUCTOR VISUAL (Canvas)
  // ───────────────────────────────────────────────────────────────────────
  
  const [visualElements, setVisualElements] = useState<CanvasElement[]>([]);
  const [visualWaspPosition, setVisualWaspPosition] = useState({ x: 400, y: 300 });
  const [visualDraggedElement, setVisualDraggedElement] = useState<any>(null);
  const [visualDraggedElementId, setVisualDraggedElementId] = useState<string | null>(null);
  const [visualDraggedWasp, setVisualDraggedWasp] = useState(false);
  const [visualParticles, setVisualParticles] = useState<any[]>([]);
  const [savedAudiences, setSavedAudiences] = useState<SavedAudience[]>([]);
  const [showCreateAudienceModal, setShowCreateAudienceModal] = useState(false);
  const [newAudienceName, setNewAudienceName] = useState('');
  
  const canvasRef = React.useRef<HTMLDivElement>(null);
  const dragOffsetRef = React.useRef({ x: 0, y: 0 });

  // ───────────────────────────────────────────────────────────────────────
  // NOTIFICACIONES
  // ───────────────────────────────────────────────────────────────────────

  const showNotification = useCallback((
    type: 'success' | 'error' | 'warning' | 'info',
    message: string,
    duration = 4000
  ) => {
    const id = `notif-${Date.now()}`;
    setNotifications(prev => [...prev, { id, type, message }]);
    
    if (duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, duration);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // FILTRADO Y ORDENAMIENTO
  // ───────────────────────────────────────────────────────────────────────

  const filteredContacts = useMemo(() => {
    let result = [...contacts];

    // Búsqueda
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(search) ||
        c.phone.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search) ||
        c.company?.toLowerCase().includes(search)
      );
    }

    // Filtro por estado
    if (filters.status !== 'all') {
      result = result.filter(c => c.status === filters.status);
    }

    // Filtro por grupo
    if (filters.group !== 'all') {
      result = result.filter(c => c.groups.includes(filters.group));
    }

    // Filtro por tag
    if (filters.tag !== 'all') {
      result = result.filter(c => c.tags.includes(filters.tag));
    }

    // Ordenamiento
    result.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'createdAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = aValue?.toString().toLowerCase() || '';
        bValue = bValue?.toString().toLowerCase() || '';
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [contacts, filters, sortField, sortDirection]);

  // ───────────────────────────────────────────────────────────────────────
  // PAGINACIÓN
  // ───────────────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  
  const paginatedContacts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredContacts.slice(start, end);
  }, [filteredContacts, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // ORDENAMIENTO
  // ───────────────────────────────────────────────────────────────────────

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return faSort;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  // ───────────────────────────────────────────────────────────────────────
  // ACCIONES
  // ───────────────────────────────────────────────────────────────────────

// Línea ~350
const handleUseSelectedInternal = (customContacts?: ContactDB[]) => {
  const contactsToUse = customContacts || getSelectedContactsData();
  
  if (contactsToUse.length === 0) {
    showNotification('warning', 'Selecciona al menos un contacto');
    return;
  }
  
  const contactsForSend = contactsToUse.map(c => ({
    nombre: c.name,
    telefono: c.phone,
    email: c.email || '',
    empresa: c.company || '',
    mensaje: `Hola ${c.name}...`
  }));

  if (onContactsSelected) {
    onContactsSelected(contactsToUse);
  }

  if (onContactsReady) {
    onContactsReady(contactsForSend);
  }

  showNotification('success', `${contactsToUse.length} contactos listos para envío`);
};

// Wrapper para botones (sin parámetros)
const handleUseSelected = () => {
  handleUseSelectedInternal();
};

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: SIDEBAR
  // ───────────────────────────────────────────────────────────────────────

  const renderSidebar = () => (
    <div className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>
      <div className={styles.sidebarHeader}>
        {!sidebarCollapsed && <h6 className={styles.sidebarTitle}>Vistas</h6>}
        <button
          className={`${styles.btn} ${styles.btnIcon}`}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Expandir' : 'Contraer'}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      </div>

      <nav className={styles.sidebarNav}>
        <div
          className={`${styles.sidebarItem} ${viewMode === 'database' ? styles.active : ''}`}
          onClick={() => setViewMode('database')}
        >
          <span className={styles.sidebarIcon}>
            <FontAwesomeIcon icon={faDatabase} />
          </span>
          {!sidebarCollapsed && <span className={styles.sidebarLabel}>Base de Datos</span>}
        </div>

        <div
          className={`${styles.sidebarItem} ${viewMode === 'import' ? styles.active : ''}`}
          onClick={() => setViewMode('import')}
        >
          <span className={styles.sidebarIcon}>
            <FontAwesomeIcon icon={faFileUpload} />
          </span>
          {!sidebarCollapsed && <span className={styles.sidebarLabel}>Importar</span>}
        </div>

        <div
          className={`${styles.sidebarItem} ${viewMode === 'visual' ? styles.active : ''}`}
          onClick={() => setViewMode('visual')}
        >
          <span className={styles.sidebarIcon}>
            <FontAwesomeIcon icon={faMagic} />
          </span>
          {!sidebarCollapsed && <span className={styles.sidebarLabel}>Constructor</span>}
        </div>

        <div
          className={`${styles.sidebarItem} ${viewMode === 'ai' ? styles.active : ''}`}
          onClick={() => setViewMode('ai')}
        >
          <span className={styles.sidebarIcon}>
            <FontAwesomeIcon icon={faRobot} />
          </span>
          {!sidebarCollapsed && <span className={styles.sidebarLabel}>IA</span>}
        </div>
      </nav>
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: HEADER
  // ───────────────────────────────────────────────────────────────────────

  const renderHeader = () => (
    <div className={styles.contentHeader}>
      <h1 className={styles.headerTitle}>
        <FontAwesomeIcon icon={faDatabase} style={{ color: '#2ca799ff' }} />
        Contactos
        {selectedContacts.length > 0 && (
          <span className={`${styles.badge} ${styles.badgeInfo}`}>
            {selectedContacts.length} seleccionados
          </span>
        )}
      </h1>

      <div className={styles.headerActions}>
        <ExportDropdown 
          contacts={filteredContacts}
          filename="archivo_contactos"
          onExportComplete={(format, count) => {
            showNotification('success', `${count} contactos exportados como ${format.toUpperCase()}`);
          }}
        />

        <button
          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSmall}`}
          onClick={() => setShowUploadModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          Importar CSV
        </button>
      </div>
      
      {/* Modales */}
      <UploadCSVModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onSuccess={(count) => {
          showNotification('success', `${count} contactos importados correctamente`);
        }}
      />

      <AddContactManualModal
        show={showAddManualModal}
        onHide={() => setShowAddManualModal(false)}
        onSuccess={(contact) => {
          showNotification('success', `Contacto "${contact.name}" agregado correctamente`);
        }}
        groups={groups}
        tags={tags}
        checkDuplicatePhone={checkDuplicatePhone}
        addContact={addContact}
      />

      <AddContactsAIModal
        show={showAIModal}
        onHide={() => setShowAIModal(false)}
        onSuccess={(contacts) => {
          showNotification('success', `Contactos agregados con IA correctamente`);
        }}
        checkDuplicatePhone={checkDuplicatePhone}
        addContact={addContact}
        existingContactsCount={contacts.length}
        existingContacts={contacts}
      />
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: TOOLBAR
  // ───────────────────────────────────────────────────────────────────────

  const renderToolbar = () => (
    <div className={styles.toolbar}>
      <div className={styles.searchBox}>
        <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
        <input
          type="text"
          className={styles.searchInput} 
          placeholder="Buscar por nombre, teléfono, email..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
      </div>

      <select
        className={styles.filterSelect}
        value={filters.status}
        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
      >
        <option value="all">Todos los estados</option>
        <option value="active">Activo</option>
        <option value="inactive">Inactivo</option>
        <option value="blocked">Bloqueado</option>
      </select>

      <select
        className={styles.filterSelect}
        value={filters.group}
        onChange={(e) => setFilters({ ...filters, group: e.target.value })}
      >
        <option value="all">Todos los grupos</option>
        {groups.map(g => (
          <option key={g.id} value={g.name}>{g.name}</option>
        ))}
      </select>

      <button
        className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
        onClick={() => setFilters({ search: '', status: 'all', group: 'all', tag: 'all' })}
      >
        <FontAwesomeIcon icon={faFilter} />
        Limpiar
      </button>

      {selectedContacts.length > 0 && (
        <>
          <button
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
            onClick={clearSelection}
          >
            <FontAwesomeIcon icon={faTimes} />
            Deseleccionar
          </button>

          <button
            className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSmall}`}
            onClick={handleUseSelected}
          >
            <FontAwesomeIcon icon={faCheck} />
            Usar Seleccionados ({selectedContacts.length})
          </button>
        </>
      )}
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: TABLE (sin cambios)
  // ───────────────────────────────────────────────────────────────────────

  const renderTable = () => (
    <>
      <div className={styles.tableContainer}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={
                    filteredContacts.length > 0 &&
                    filteredContacts.every(c => selectedContacts.includes(c.id))
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      selectAll();
                    } else {
                      clearSelection();
                    }
                  }}
                />
              </th>
              <th className={styles.sortable} onClick={() => handleSort('name')}>
                Nombre
                <FontAwesomeIcon
                  icon={getSortIcon('name')}
                  style={{ marginLeft: '8px', fontSize: '0.75rem' }}
                />
              </th>
              <th className={styles.sortable} onClick={() => handleSort('phone')}>
                Teléfono
                <FontAwesomeIcon
                  icon={getSortIcon('phone')}
                  style={{ marginLeft: '8px', fontSize: '0.75rem' }}
                />
              </th>
              <th className={styles.sortable} onClick={() => handleSort('email')}>
                Email
                <FontAwesomeIcon
                  icon={getSortIcon('email')}
                  style={{ marginLeft: '8px', fontSize: '0.75rem' }}
                />
              </th>
              <th>Grupos</th>
              <th className={styles.sortable} onClick={() => handleSort('status')}>
                Estado
                <FontAwesomeIcon
                  icon={getSortIcon('status')}
                  style={{ marginLeft: '8px', fontSize: '0.75rem' }}
                />
              </th>
              <th style={{ width: '60px' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                  <Spinner animation="border" size="sm" /> Cargando...
                </td>
              </tr>
            ) : paginatedContacts.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--hs-gray-600)' }}>
                  {filters.search || filters.status !== 'all' || filters.group !== 'all'
                    ? 'No se encontraron contactos con estos filtros'
                    : 'No hay contactos en la base de datos'}
                </td>
              </tr>
            ) : (
              paginatedContacts.map((contact) => (
                <tr
                  key={contact.id}
                  className={selectedContacts.includes(contact.id) ? styles.selected : ''}
                >
                  <td>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => toggleContactSelection(contact.id)}
                    />
                  </td>
                  <td className={styles.cellName}>{contact.name}</td>
                  <td className={styles.cellPhone}>{contact.phone}</td>
                  <td>
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className={styles.cellEmail}>
                        {contact.email}
                      </a>
                    ) : (
                      <span className={styles.textMuted}>-</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.tagGroup}>
                      {contact.groups.length > 0 ? (
                        contact.groups.slice(0, 2).map((group, idx) => (
                          <span key={idx} className={styles.tag}>
                            {group}
                          </span>
                        ))
                      ) : (
                        <span className={styles.textMuted}>-</span>
                      )}
                      {contact.groups.length > 2 && (
                        <span className={styles.tag}>+{contact.groups.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`${styles.badge} ${
                        contact.status === 'active' ? styles.badgeSuccess :
                        contact.status === 'inactive' ? styles.badgeWarning :
                        styles.badgeDanger
                      }`}
                    >
                      {contact.status === 'active' ? '🟢 Activo' :
                       contact.status === 'inactive' ? '🟡 Inactivo' :
                       '🔴 Bloqueado'}
                    </span>
                  </td>
                  <td>
                    <button className={`${styles.btn} ${styles.btnIcon} ${styles.btnSmall}`}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <div className={styles.paginationInfo}>
            Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredContacts.length)} de {filteredContacts.length}
          </div>

          <div className={styles.paginationControls}>
            <button
              className={styles.pageButton}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  className={`${styles.pageButton} ${currentPage === pageNum ? styles.active : ''}`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              className={styles.pageButton}
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          <select
            className={styles.filterSelect}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ width: 'auto' }}
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
        </div>
      )}
    </>
  );

  // ───────────────────────────────────────────────────────────────────────
  // VISUAL CANVAS: HANDLERS DRAG & DROP
  // ───────────────────────────────────────────────────────────────────────

  const handleDragStartElement = useCallback((e: React.DragEvent, type: string, label: string, icon: string, color: string) => {
    const newElement = {
      id: `${type}-${Date.now()}`,
      type,
      label,
      icon,
      position: { x: 0, y: 0 },
      color
    };

    setVisualDraggedElement(newElement);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', newElement.id);
  }, []);

  const handleDropCanvas = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    
    if (!visualDraggedElement || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - 60;
    const y = e.clientY - rect.top - 20;

    const element = {
      ...visualDraggedElement,
      position: { x, y }
    };

    setVisualElements(prev => [...prev, element]);
    createParticlesForElement(element.id, element.color);
    setVisualDraggedElement(null);
  }, [visualDraggedElement]);

  const handleDragOverCanvas = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleElementMouseDown = useCallback((e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    const element = visualElements.find(el => el.id === elementId);
    if (!element) return;

    const rect = e.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };

    setVisualDraggedElementId(elementId);
  }, [visualElements]);

  const handleWaspMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };

    setVisualDraggedWasp(true);
  }, []);

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - dragOffsetRef.current.x;
    const y = e.clientY - rect.top - dragOffsetRef.current.y;

    if (visualDraggedWasp) {
      setVisualWaspPosition({ x, y });
    } else if (visualDraggedElementId) {
      setVisualElements(prev => prev.map(el =>
        el.id === visualDraggedElementId ? { ...el, position: { x, y } } : el
      ));
    }
  }, [visualDraggedWasp, visualDraggedElementId]);

  const handleCanvasMouseUp = useCallback(() => {
    setVisualDraggedWasp(false);
    setVisualDraggedElementId(null);
  }, []);

  const handleDeleteElement = useCallback((elementId: string) => {
    setVisualElements(prev => prev.filter(e => e.id !== elementId));
    setVisualParticles(prev => prev.filter(p => p.elementId !== elementId));
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // VISUAL CANVAS: SISTEMA DE PARTÍCULAS
  // ───────────────────────────────────────────────────────────────────────

  const createParticlesForElement = useCallback((elementId: string, color: string) => {
    const PARTICLE_COUNT = 3;
    const newParticles: any[] = [];
    
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      newParticles.push({
        id: `${elementId}-particle-${i}`,
        elementId,
        progress: i / PARTICLE_COUNT,
        speed: 0.008 + Math.random() * 0.004,
        color
      });
    }
    
    setVisualParticles(prev => [...prev.filter(p => p.elementId !== elementId), ...newParticles]);
  }, []);

  // Animación de partículas
  React.useEffect(() => {
    if (visualParticles.length === 0) return;

    const interval = setInterval(() => {
      setVisualParticles(prev => prev.map(p => ({
        ...p,
        progress: (p.progress + p.speed) % 1
      })));
    }, 16);

    return () => clearInterval(interval);
  }, [visualParticles.length]);

  // ───────────────────────────────────────────────────────────────────────
  // VISUAL CANVAS: AUDIENCIAS
  // ───────────────────────────────────────────────────────────────────────

  const handleCreateAudience = useCallback(() => {
    if (!newAudienceName.trim()) {
      showNotification('error', 'Debes ingresar un nombre para la audiencia');
      return;
    }

    const newAudience: SavedAudience = {
      id: `audience-${Date.now()}`,
      name: newAudienceName.trim(),
      filters: visualElements.map(el => ({
        type: el.type as any,
        value: el.data,
        label: el.label
      })),
      contactCount: visualElements.reduce((acc, el) => acc + (el.data?.contactIds?.length || 0), 0),
      createdAt: new Date().toISOString(),
      color: `hsl(${Math.random() * 360}, 70%, 50%)`
    };

    setSavedAudiences(prev => [...prev, newAudience]);
    setShowCreateAudienceModal(false);
    setNewAudienceName('');
    showNotification('success', `Audiencia "${newAudience.name}" creada con éxito`);
  }, [newAudienceName, visualElements, showNotification]);

  const handleDeleteAudience = useCallback((audienceId: string) => {
    setSavedAudiences(prev => prev.filter(a => a.id !== audienceId));
    showNotification('info', 'Audiencia eliminada');
  }, [showNotification]);

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: VISUAL BUILDER - ACTUALIZADO CON GEOMAP FUNCIONAL
  // ───────────────────────────────────────────────────────────────────────

  const renderVisualBuilder = () => {
    // Si está activo el mapa, renderizar ContactsGeoBuilder
    if (showGeoMap) {
      return (
        <ContactsGeoBuilder
          contacts={contactsForGeo}
          selectedContactIds={selectedContacts}
          onContactSelect={(idOrContact) => {
            const id = typeof idOrContact === 'string' ? idOrContact : idOrContact.id;
            toggleContactSelection(id);
          }}
          onSelectionComplete={(finalContacts: ContactDB[]) => {
            setGeoBuilderContacts(finalContacts);
            handleUseSelectedInternal(finalContacts);  // ← Usar internal con parámetro
          }}
          onClose={() => setShowGeoMap(false)}
        />
      );
    }

    // Vista por defecto (constructor visual)
    return (
      <div className={styles.flowBuilder}>
        <div className={styles.flowPalette}>
          <h6 className={styles.paletteTitle}>Herramientas</h6>
          
          {/* Favoritos - DRAGGABLE */}
          <div 
            className={styles.paletteItem}
            draggable
            onDragStart={(e) => handleDragStartElement(e, 'favorites', 'Favoritos', '⭐', '#f59e0b')}
            style={{ cursor: 'grab' }}
          >
            <span>⭐</span>
            <span>Favoritos</span>
            <small style={{ fontSize: '0.65rem', opacity: 0.6 }}>{favoritesContacts.length} contactos</small>
          </div>
          
          {/* Todos los Contactos - DRAGGABLE */}
          <div 
            className={styles.paletteItem}
            draggable
            onDragStart={(e) => handleDragStartElement(e, 'all-contacts', 'Todos los Contactos', '👥', '#2ca799')}
            style={{ cursor: 'grab' }}
          >
            <span>👥</span>
            <span>Todos</span>
            <small style={{ fontSize: '0.65rem', opacity: 0.6 }}>{contacts.length} contactos</small>
          </div>

          {/* Grupos - DRAGGABLE */}
          {groups.length > 0 && groups.map(group => (
            <div 
              key={group.id}
              className={styles.paletteItem}
              draggable
              onDragStart={(e) => handleDragStartElement(e, 'group', group.name, '👥', '#3b82f6')}
              style={{ cursor: 'grab' }}
            >
              <span>👥</span>
              <span>{group.name}</span>
              <small style={{ fontSize: '0.65rem', opacity: 0.6 }}>{group.contact_count} miembros</small>
            </div>
          ))}

          <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid var(--hs-gray-200)' }} />
          
          {/* Audiencias Guardadas */}
          {savedAudiences.length > 0 && (
            <>
              <h6 className={styles.paletteTitle}>Audiencias Guardadas</h6>
              {savedAudiences.map(audience => (
                <div 
                  key={audience.id}
                  className={styles.paletteItem}
                  draggable
                  onDragStart={(e) => handleDragStartElement(e, 'audience', audience.name, '🎯', audience.color)}
                  style={{ cursor: 'grab', position: 'relative' }}
                >
                  <span>🎯</span>
                  <span>{audience.name}</span>
                  <small style={{ fontSize: '0.65rem', opacity: 0.6 }}>{audience.contactCount} contactos</small>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAudience(audience.id);
                    }}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--hs-red)',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} size="sm" />
                  </button>
                </div>
              ))}
              <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid var(--hs-gray-200)' }} />
            </>
          )}
          
          <h6 className={styles.paletteTitle}>Acciones Rápidas</h6>
          
          <button 
            className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSmall}`}
            onClick={() => setShowAddManualModal(true)}
            style={{ width: '100%', marginBottom: '8px' }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Agregar Contacto
          </button>
          
          {/* ✅ BOTÓN NUEVA AUDIENCIA - SIEMPRE DISPONIBLE */}
          <button 
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
            onClick={() => setShowCreateAudienceModal(true)}
            style={{ width: '100%' }}
          >
            <FontAwesomeIcon icon={faUsers} className="me-2" />
            Nueva Audiencia
          </button>
        </div>

        <div 
          ref={canvasRef}
          className={styles.flowCanvas}
          onDrop={handleDropCanvas}
          onDragOver={handleDragOverCanvas}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp}
          style={{
            position: 'relative',
            backgroundImage: 'radial-gradient(circle, #e0e0e0 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        >
          {/* Botones superiores */}
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            display: 'flex',
            gap: '12px',
            zIndex: 10
          }}>
            {/* Botón Finalizar - Estilo igual que GeoMap */}
            {visualElements.length > 0 && (
              <button 
                className={`${styles.btn} ${styles.btnSuccess}`}
                onClick={() => {
                  // Calcular contactos únicos de todos los elementos (OR lógico)
                  const allContactIds = new Set<string>();
                  
                  visualElements.forEach(el => {
                    if (el.type === 'favorites') {
                      favoritesContacts.forEach(c => allContactIds.add(c.id));
                    } else if (el.type === 'all-contacts') {
                      contacts.forEach(c => allContactIds.add(c.id));
                    } else if (el.type === 'group' && el.data?.groupId) {
                      // Filtrar contactos del grupo
                      contacts
                        .filter(c => c.groups.includes(el.data.groupId))
                        .forEach(c => allContactIds.add(c.id));
                    } else if (el.type === 'audience' && el.data?.audienceId) {
                      const audience = savedAudiences.find(a => a.id === el.data.audienceId);
                      // Aplicar filtros de la audiencia (simplificado)
                      contacts.forEach(c => allContactIds.add(c.id));
                    }
                  });
                  
                  // Incluir mockMatilde si está en favoritos y fue seleccionada
                  const finalContacts = contactsForGeo.filter(c => allContactIds.has(c.id));
                  
                  if (finalContacts.length === 0) {
                    showNotification('warning', 'No hay contactos que cumplan los filtros');
                    return;
                  }
                  
                  // Usar la misma función que GeoMap
                  handleUseSelectedInternal(finalContacts);
                  
                  // Limpiar canvas
                  setVisualElements([]);
                  setVisualParticles([]);
                  
                  // Volver a vista database (como lo hace GeoMap)
                  setViewMode('database');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Usar Seleccionados
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  {Array.from(
                    visualElements.reduce((acc, el) => {
                      if (el.type === 'favorites') {
                        favoritesContacts.forEach(c => acc.add(c.id));
                      } else if (el.type === 'all-contacts') {
                        contacts.forEach(c => acc.add(c.id));
                      } else if (el.type === 'group' && el.data?.groupId) {
                        contacts
                          .filter(c => c.groups.includes(el.data.groupId))
                          .forEach(c => acc.add(c.id));
                      }
                      return acc;
                    }, new Set<string>())
                  ).length}
                </span>
              </button>
            )}

            <button 
              className={`${styles.btn} ${styles.btnSecondary}`}
              title="Tutorial del Constructor Visual"
              disabled
              style={{ opacity: 0.6 }}
            >
              <FontAwesomeIcon icon={faMagic} style={{ marginRight: '8px' }} />
              Tutorial
              <small style={{ marginLeft: '6px', fontSize: '0.7rem' }}>(Próximamente)</small>
            </button>

            {/* ← BOTÓN ACTUALIZADO: Ahora abre el mapa */}
            <button 
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => setShowGeoMap(true)}
              title="Mapa Geográfico Interactivo"
            >
              <FontAwesomeIcon icon={faMapMarkedAlt} style={{ marginRight: '8px' }} />
              GeoMap
            </button>
          </div>

          {/* SVG Layer - Líneas con partículas */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 1
            }}
          >
            {/* Líneas de conexión */}
            {visualElements.map(element => (
              <line
                key={element.id}
                x1={element.position.x + 60}
                y1={element.position.y + 20}
                x2={visualWaspPosition.x + 40}
                y2={visualWaspPosition.y + 40}
                stroke={element.color}
                strokeWidth="2"
                strokeDasharray="5,5"
                opacity="0.5"
              />
            ))}
            
            {/* Partículas animadas */}
            {visualParticles.map(particle => {
              const element = visualElements.find(el => el.id === particle.elementId);
              if (!element) return null;

              const x1 = element.position.x + 60;
              const y1 = element.position.y + 20;
              const x2 = visualWaspPosition.x + 40;
              const y2 = visualWaspPosition.y + 40;

              const x = x1 + (x2 - x1) * particle.progress;
              const y = y1 + (y2 - y1) * particle.progress;

              return (
                <circle
                  key={particle.id}
                  cx={x}
                  cy={y}
                  r="3"
                  fill={particle.color}
                  opacity="0.8"
                />
              );
            })}
          </svg>

          {/* WASP Madre - DRAGGABLE */}
          <div
            onMouseDown={handleWaspMouseDown}
            style={{
              position: 'absolute',
              left: visualWaspPosition.x,
              top: visualWaspPosition.y,
              width: 80,
              height: 80,
              zIndex: 2,
              cursor: visualDraggedWasp ? 'grabbing' : 'grab',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none'
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'white',
                border: '3px solid var(--hs-orange)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                boxShadow: 'var(--hs-shadow-md)',
                position: 'relative'
              }}
            >
              🐝
              {visualElements.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: -8,
                    right: -8,
                    width: 24,
                    height: 24,
                    backgroundColor: 'var(--hs-green)',
                    border: '2px solid white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    color: 'white'
                  }}
                >
                  {visualElements.length}
                </div>
              )}
            </div>
            <div
              style={{
                marginTop: '8px',
                padding: '6px 12px',
                backgroundColor: 'var(--hs-orange)',
                color: 'white',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                boxShadow: 'var(--hs-shadow-sm)',
                whiteSpace: 'nowrap'
              }}
            >
              WASP
            </div>
          </div>

          {/* Elementos Soltados - DRAGGABLES */}
          {visualElements.map(element => (
            <div
              key={element.id}
              onMouseDown={(e) => handleElementMouseDown(e, element.id)}
              style={{
                position: 'absolute',
                left: element.position.x,
                top: element.position.y,
                padding: '12px 16px',
                backgroundColor: 'white',
                border: `2px solid ${element.color}`,
                borderRadius: '8px',
                cursor: visualDraggedElementId === element.id ? 'grabbing' : 'grab',
                zIndex: 3,
                boxShadow: 'var(--hs-shadow-md)',
                minWidth: '140px',
                userSelect: 'none'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '1.2rem' }}>{element.icon}</span>
                <span style={{ fontWeight: 600, fontSize: '0.813rem', color: 'var(--hs-slate)' }}>
                  {element.label}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteElement(element.id);
                  }}
                  style={{
                    marginLeft: 'auto',
                    background: 'none',
                    border: 'none',
                    color: 'var(--hs-red)',
                    cursor: 'pointer',
                    padding: '2px'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} size="sm" />
                </button>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {visualElements.length === 0 && (
            <div style={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: 'var(--hs-gray-600)',
              maxWidth: '500px',
              padding: '32px'
            }}>
              <svg 
                width="120" 
                height="120" 
                viewBox="0 0 120 120" 
                style={{ marginBottom: '24px', opacity: 0.3 }}
              >
                <circle cx="40" cy="35" r="15" fill="#2ca799" opacity="0.7"/>
                <path d="M 20 65 Q 20 50 40 50 Q 60 50 60 65 Z" fill="#2ca799" opacity="0.7"/>
                
                <circle cx="80" cy="35" r="15" fill="#3b82f6" opacity="0.7"/>
                <path d="M 60 65 Q 60 50 80 50 Q 100 50 100 65 Z" fill="#3b82f6" opacity="0.7"/>
                
                <circle cx="60" cy="60" r="18" fill="#10b981" opacity="0.8"/>
                <path d="M 35 95 Q 35 75 60 75 Q 85 75 85 95 Z" fill="#10b981" opacity="0.8"/>
                
                <line x1="40" y1="50" x2="60" y2="75" stroke="#2ca799" strokeWidth="2" opacity="0.3" strokeDasharray="5,5"/>
                <line x1="80" y1="50" x2="60" y2="75" stroke="#3b82f6" strokeWidth="2" opacity="0.3" strokeDasharray="5,5"/>
              </svg>

              <h5 style={{ fontWeight: 600, marginBottom: '12px', color: 'var(--bs-body-color)' }}>
                Constructor de Audiencias
              </h5>
              
              <p style={{ fontSize: '0.875rem', marginBottom: '24px', lineHeight: '1.6' }}>
                Arrastra elementos desde el sidebar para segmentar contactos.<br />
                Usa <strong>"GeoMap"</strong> para targeting geográfico avanzado.
              </p>

              <div style={{
                backgroundColor: 'var(--hs-teal-light)',
                border: '1px solid var(--hs-teal)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'left',
                marginTop: '24px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '0.813rem', color: 'var(--hs-teal-dark)' }}>
                  ✅ Disponible Ahora:
                </div>
                <ul style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--hs-slate)',
                  paddingLeft: '20px',
                  margin: 0,
                  lineHeight: '1.8'
                }}>
                  <li>Drag & drop de grupos y contactos</li>
                  <li>Crear audiencias personalizadas</li>
                  <li>Mapa geográfico interactivo</li>
                  <li>Animaciones de flujo de datos</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: IMPORT MODE (sin cambios)
  // ───────────────────────────────────────────────────────────────────────

  const renderImportMode = () => (
    <div style={{ 
      flex: 1, 
      padding: 'var(--hs-spacing-xl)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <div style={{ 
        maxWidth: '600px', 
        width: '100%',
        backgroundColor: 'white',
        padding: '48px',
        borderRadius: '8px',
        border: 'var(--hs-border)',
        boxShadow: 'var(--hs-shadow-md)',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '24px' }}>
          <FontAwesomeIcon icon={faFileUpload} style={{ color: 'var(--hs-orange)' }} />
        </div>
        
        <h3 style={{ fontWeight: 600, marginBottom: '12px', color: 'var(--hs-slate)' }}>
          Importar Contactos
        </h3>
        
        <p style={{ color: 'var(--hs-gray-600)', marginBottom: '32px', fontSize: '0.875rem' }}>
          Sube tu archivo CSV o Excel para agregar contactos masivamente.
          Los contactos duplicados serán detectados automáticamente.
        </p>

        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => setShowUploadModal(true)}
          style={{ marginBottom: '16px' }}
        >
          <FontAwesomeIcon icon={faFileUpload} className="me-2" />
          Seleccionar Archivo
        </button>

        <div style={{ 
          marginTop: '32px',
          padding: '20px',
          backgroundColor: 'var(--hs-gray-50)',
          borderRadius: '6px',
          textAlign: 'left'
        }}>
          <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '0.813rem' }}>
            📋 Formato requerido:
          </h6>
          <ul style={{ 
            fontSize: '0.813rem', 
            color: 'var(--hs-gray-600)',
            paddingLeft: '20px',
            margin: 0
          }}>
            <li>Primera fila con encabezados (nombre, teléfono, email)</li>
            <li>Teléfonos con formato +56912345678</li>
            <li>Máximo 10,000 contactos por archivo</li>
            <li>Formatos: CSV, XLSX, XLS</li>
          </ul>
        </div>

        <div style={{ marginTop: '24px' }}>
          <button className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}>
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Descargar Plantilla
          </button>
        </div>
      </div>
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: AI MODE (sin cambios)
  // ───────────────────────────────────────────────────────────────────────

  const renderAIMode = () => (
    <div style={{ 
      flex: 1, 
      padding: 'var(--hs-spacing-xl)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <div style={{ 
        maxWidth: '700px', 
        width: '100%',
        backgroundColor: 'white',
        padding: '48px',
        borderRadius: '8px',
        border: 'var(--hs-border)',
        boxShadow: 'var(--hs-shadow-md)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>
            <FontAwesomeIcon icon={faRobot} style={{ color: '#2ca799' }} />
          </div>
          
          <h3 style={{ fontWeight: 600, marginBottom: '12px', color: 'var(--hs-slate)' }}>
            Agregar Contactos con IA
          </h3>
          
          <p style={{ color: 'var(--hs-gray-600)', fontSize: '0.875rem' }}>
            Pega texto con información de contactos y la IA los extraerá automáticamente
          </p>
        </div>

        <button 
          className={`${styles.btn} ${styles.btnPrimary}`} 
          style={{ width: '100%', padding: '16px', fontSize: '1rem' }}
          onClick={() => setShowAIModal(true)}
        >
          <FontAwesomeIcon icon={faRobot} className="me-2" />
          Comenzar Extracción con IA
        </button>

        <div style={{ 
          marginTop: '32px',
          padding: '20px',
          backgroundColor: 'rgba(44, 167, 153, 0.1)',
          borderRadius: '6px',
          border: '1px solid #2ca799'
        }}>
          <h6 style={{ 
            fontWeight: 600, 
            marginBottom: '12px', 
            fontSize: '0.875rem',
            color: '#2ca799'
          }}>
            💡 La IA puede extraer:
          </h6>
          <ul style={{ 
            fontSize: '0.813rem', 
            color: 'var(--hs-slate)',
            paddingLeft: '20px',
            margin: 0,
            lineHeight: '1.8'
          }}>
            <li>Nombres completos desde cualquier formato</li>
            <li>Teléfonos chilenos (normaliza a +56XXXXXXXXX)</li>
            <li>Emails y correos electrónicos</li>
            <li>Empresas y organizaciones</li>
            <li>Cargos y posiciones laborales</li>
          </ul>
        </div>

        <div style={{ 
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'var(--hs-gray-50)',
          borderRadius: '6px'
        }}>
          <h6 style={{ 
            fontWeight: 600, 
            marginBottom: '12px', 
            fontSize: '0.813rem'
          }}>
            📝 Ejemplos de texto:
          </h6>
          <div style={{ 
            fontSize: '0.75rem', 
            color: 'var(--hs-gray-600)',
            fontFamily: 'monospace',
            backgroundColor: 'white',
            padding: '12px',
            borderRadius: '4px',
            border: '1px solid var(--bs-border-color)'
          }}>
            Juan Pérez, gerente de ventas en Acme Corp,<br/>
            teléfono +56912345678, juan@acme.cl<br/>
            <br/>
            María González, directora marketing Tech,<br/>
            correo maria@tech.cl, fono 912345679
          </div>
        </div>
      </div>
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: NOTIFICACIONES (sin cambios)
  // ───────────────────────────────────────────────────────────────────────

  const renderNotifications = () => (
    <div style={{ 
      position: 'fixed', 
      top: 20, 
      right: 20, 
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    }}>
      {notifications.map(notif => (
        <Alert
          key={notif.id}
          variant={notif.type}
          dismissible
          onClose={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))}
          className={styles.fadeIn}
          style={{
            minWidth: '300px',
            boxShadow: 'var(--hs-shadow-lg)',
            fontSize: '0.875rem',
            margin: 0
          }}
        >
          <FontAwesomeIcon 
            icon={
              notif.type === 'success' ? faCheckCircle :
              notif.type === 'error' ? faTimesCircle :
              notif.type === 'warning' ? faExclamationTriangle :
              faCheckCircle
            }
            className="me-2"
          />
          {notif.message}
        </Alert>
      ))}
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ───────────────────────────────────────────────────────────────────────

  return (
    <div className={styles.contactsHubspot}>
      {/* CSS Global para Modal Backdrop */}
      <style>{`
        .modal-backdrop.show {
          z-index: 99998 !important;
        }
        .modal.show {
          z-index: 99999 !important;
        }
        .modal-highest-z {
          z-index: 100000 !important;
        }
      `}</style>
      
      {renderNotifications()}
      {renderSidebar()}
      
      <div className={styles.mainContent}>
        {renderHeader()}
        
        {viewMode === 'database' && (
          <>
            {renderToolbar()}
            {renderTable()}
          </>
        )}
        
        {viewMode === 'import' && renderImportMode()}
        {viewMode === 'visual' && renderVisualBuilder()}
        {viewMode === 'ai' && renderAIMode()}
      </div>

      {/* Modal: Crear Nueva Audiencia - Z-INDEX MÁXIMO */}
      <Modal 
        show={showCreateAudienceModal} 
        onHide={() => setShowCreateAudienceModal(false)}
        style={{ zIndex: 99999 }}
        dialogClassName="modal-highest-z"
      >
        <div style={{ zIndex: 100000, position: 'relative' }}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faUsers} className="me-2" />
              Nueva Audiencia
            </Modal.Title>
          </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              Nombre de la Audiencia
            </label>
            <input
              type="text"
              value={newAudienceName}
              onChange={(e) => setNewAudienceName(e.target.value)}
              placeholder="Ej: Clientes VIP Santiago"
              className="form-control"
              autoFocus
            />
          </div>
          
          <div style={{
            padding: '12px',
            backgroundColor: '#f3f4f6',
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '8px' }}>
              Filtros Actuales:
            </div>
            {visualElements.length === 0 ? (
              <div style={{ fontSize: '0.813rem', color: '#6b7280' }}>
                No hay filtros en el canvas
              </div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.813rem' }}>
                {visualElements.map(el => (
                  <li key={el.id}>
                    {el.icon} {el.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button 
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setShowCreateAudienceModal(false)}
          >
            Cancelar
          </button>
          <button 
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleCreateAudience}
            disabled={!newAudienceName.trim()}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Crear Audiencia
          </button>
        </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default ContactsHubSpot;